import React, { useRef, useState, useCallback, useMemo } from "react";

interface SearchSuggestion {
  id: string;
  name: string;
  type: "folder" | "audio" | "video" | "image" | "vector";
  folderName?: string;
  folderColor?: string;
  itemCount?: number;
}

interface LibrarySearchBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSearchSubmit: () => void;
  onFilterClick: () => void;
  placeholder?: string;
  ti18n?: Record<string, string>;
  filterElement?: React.ReactNode;
  suggestions?: SearchSuggestion[];
  onSuggestionClick?: (suggestion: SearchSuggestion) => void;
  showSuggestions?: boolean;
  itemSectionLabel?: string;
  folderSectionLabel?: string;
}

const t = (ti18n: Record<string, string> | undefined, key: string): string => {
  if (ti18n) {
    return window.getI18n(ti18n, key);
  }
  return key;
};

const ICON_STYLE: React.CSSProperties = {
  WebkitTextStroke: "0.5px",
  fontSize: "var(--text-lg)",
  color: "var(--text-muted)",
  lineHeight: 1,
  flexShrink: 0,
};

const INTERACTIVE_ICON_BASE: React.CSSProperties = {
  ...ICON_STYLE,
  background: "none",
  border: "none",
  padding: 0,
  margin: 0,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const DROPDOWN_STYLE: React.CSSProperties = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  background: "var(--bg-default, #fff)",
  border: "1px solid var(--border-default-color, #ddd)",
  borderTop: "none",
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  zIndex: 100,
  maxHeight: 320,
  overflowY: "auto",
  boxSizing: "border-box",
};

const SECTION_LABEL_STYLE: React.CSSProperties = {
  padding: "6px 16px",
  fontSize: "var(--text-sm, 12px)",
  fontWeight: 700,
  color: "var(--text-muted, #888)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  lineHeight: 1.4,
};

const SUGGESTION_ROW_STYLE: React.CSSProperties = {
  padding: "8px 16px",
  display: "flex",
  alignItems: "center",
  gap: 10,
  cursor: "pointer",
  transition: "background 0.15s ease",
};

const SUGGESTION_ICON_STYLE: React.CSSProperties = {
  fontSize: "var(--text-lg, 18px)",
  WebkitTextStroke: "0.5px",
  lineHeight: 1,
  flexShrink: 0,
};

const SUGGESTION_NAME_STYLE: React.CSSProperties = {
  fontSize: "var(--text-base, 14px)",
  color: "var(--text-default-color, #222)",
  flex: 1,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const SUGGESTION_META_STYLE: React.CSSProperties = {
  fontSize: "var(--text-sm, 12px)",
  color: "var(--text-muted, #888)",
  flexShrink: 0,
  whiteSpace: "nowrap",
};

const FOOTER_ROW_STYLE: React.CSSProperties = {
  padding: "10px 16px",
  display: "flex",
  alignItems: "center",
  gap: 10,
  cursor: "pointer",
  borderTop: "1px solid var(--border-default-color, #ddd)",
  transition: "background 0.15s ease",
};

/**
 * Renders a suggestion name with the matching query portion bolded.
 */
function HighlightedName({ name, query }: { name: string; query: string }) {
  if (!query) {
    return <span style={SUGGESTION_NAME_STYLE}>{name}</span>;
  }
  const lowerName = name.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const matchIndex = lowerName.indexOf(lowerQuery);
  if (matchIndex === -1) {
    return <span style={SUGGESTION_NAME_STYLE}>{name}</span>;
  }
  const before = name.slice(0, matchIndex);
  const match = name.slice(matchIndex, matchIndex + query.length);
  const after = name.slice(matchIndex + query.length);
  return (
    <span style={SUGGESTION_NAME_STYLE}>
      {before}
      <strong>{match}</strong>
      {after}
    </span>
  );
}

const LibrarySearchBar: React.FC<LibrarySearchBarProps> = ({
  searchQuery,
  onSearchChange,
  onSearchSubmit,
  onFilterClick,
  placeholder = "Search...",
  ti18n,
  filterElement,
  suggestions = [],
  onSuggestionClick,
  showSuggestions = false,
  itemSectionLabel,
  folderSectionLabel,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange(e.target.value);
    },
    [onSearchChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        onSearchSubmit();
      }
    },
    [onSearchSubmit]
  );

  const handleClear = useCallback(() => {
    onSearchChange("");
    inputRef.current?.focus();
  }, [onSearchChange]);

  const handleFocus = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    // Small timeout to allow click events on suggestions to fire before closing
    blurTimeoutRef.current = setTimeout(() => {
      setIsFocused(false);
      setHoveredIndex(null);
    }, 200);
  }, []);

  const handleSuggestionClick = useCallback(
    (suggestion: SearchSuggestion) => {
      if (onSuggestionClick) {
        onSuggestionClick(suggestion);
      }
      setIsFocused(false);
      setHoveredIndex(null);
    },
    [onSuggestionClick]
  );

  const handleFooterClick = useCallback(() => {
    onSearchSubmit();
    setIsFocused(false);
    setHoveredIndex(null);
  }, [onSearchSubmit]);

  const hasQuery = searchQuery.length > 0;

  // Group suggestions by type: folders first, then media items
  const groupedSuggestions = useMemo(() => {
    const folders = suggestions.filter((s) => s.type === "folder");
    const projects = suggestions.filter(
      (s) =>
        s.type === "audio" ||
        s.type === "video" ||
        s.type === "image" ||
        s.type === "vector"
    );
    return { folders, projects };
  }, [suggestions]);

  const shouldShowDropdown =
    showSuggestions && isFocused && hasQuery && suggestions.length > 0;

  const containerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: 40,
    padding: "0 12px",
    gap: 8,
    background: "var(--bg-default, #fff)",
    border: isFocused
      ? "1px solid var(--trebble-primary, #EC7F54)"
      : "1px solid var(--border-default-color, #ddd)",
    boxShadow: isFocused
      ? "0 0 0 2px rgba(236, 127, 84, 0.15)"
      : "none",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    boxSizing: "border-box",
  };

  const inputStyle: React.CSSProperties = {
    flex: 1,
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: "var(--text-base, 14px)",
    color: "var(--text-default-color, #222)",
    padding: 0,
    margin: 0,
    lineHeight: "40px",
    fontFamily: "inherit",
  };

  // Build a flat list of all suggestion items for hover index tracking
  let flatIndex = 0;

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <div
        style={containerStyle}
        onClick={() => inputRef.current?.focus()}
      >
        {/* Search icon */}
        <i className="pe-7s-search" style={ICON_STYLE} />

        {/* Text input */}
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          style={inputStyle}
        />

        {/* Clear button -- only visible when there is text */}
        {hasQuery && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            style={INTERACTIVE_ICON_BASE}
          >
            <i className="pe-7s-close" />
          </button>
        )}

        {/* Filter icon — optionally wrapped by filterElement (e.g. popup) */}
        {filterElement || (
          <button
            type="button"
            onClick={onFilterClick}
            aria-label="Open filters"
            style={INTERACTIVE_ICON_BASE}
          >
            <i className="pe-7s-filter" style={ICON_STYLE} />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {shouldShowDropdown && (
        <div style={DROPDOWN_STYLE}>
          {/* Folders section */}
          {groupedSuggestions.folders.length > 0 && (
            <div>
              <div style={SECTION_LABEL_STYLE}>{folderSectionLabel || t(ti18n, "FOLDERS")}</div>
              {groupedSuggestions.folders.map((suggestion) => {
                const currentIndex = flatIndex++;
                return (
                  <div
                    key={suggestion.id}
                    style={{
                      ...SUGGESTION_ROW_STYLE,
                      background:
                        hoveredIndex === currentIndex
                          ? "var(--color-black-10, rgba(0,0,0,0.06))"
                          : "transparent",
                    }}
                    onMouseEnter={() => setHoveredIndex(currentIndex)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <i
                      className="pe-7s-folder"
                      style={{
                        ...SUGGESTION_ICON_STYLE,
                        color: suggestion.folderColor || "var(--text-muted, #888)",
                      }}
                    />
                    <HighlightedName name={suggestion.name} query={searchQuery} />
                    {suggestion.itemCount != null && (
                      <span style={SUGGESTION_META_STYLE}>
                        {suggestion.itemCount} item{suggestion.itemCount !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Projects section */}
          {groupedSuggestions.projects.length > 0 && (
            <div>
              <div style={SECTION_LABEL_STYLE}>{itemSectionLabel || t(ti18n, "PROJECTS")}</div>
              {groupedSuggestions.projects.map((suggestion) => {
                const currentIndex = flatIndex++;
                const iconClass =
                  suggestion.type === "video"
                    ? "pe-7s-video"
                    : suggestion.type === "image"
                      ? "pe-7s-photo"
                      : suggestion.type === "vector"
                        ? "pe-7s-paint-bucket"
                        : "pe-7s-music";
                return (
                  <div
                    key={suggestion.id}
                    style={{
                      ...SUGGESTION_ROW_STYLE,
                      background:
                        hoveredIndex === currentIndex
                          ? "var(--color-black-10, rgba(0,0,0,0.06))"
                          : "transparent",
                    }}
                    onMouseEnter={() => setHoveredIndex(currentIndex)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <i
                      className={iconClass}
                      style={{
                        ...SUGGESTION_ICON_STYLE,
                        color: "var(--text-muted, #888)",
                      }}
                    />
                    <HighlightedName name={suggestion.name} query={searchQuery} />
                    {suggestion.folderName && (
                      <span style={SUGGESTION_META_STYLE}>
                        {suggestion.folderName}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer: "Search for '...'" */}
          <div
            style={{
              ...FOOTER_ROW_STYLE,
              background:
                hoveredIndex === -1
                  ? "var(--color-black-10, rgba(0,0,0,0.06))"
                  : "transparent",
            }}
            onMouseEnter={() => setHoveredIndex(-1)}
            onMouseLeave={() => setHoveredIndex(null)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleFooterClick}
          >
            <i
              className="pe-7s-search"
              style={{
                ...SUGGESTION_ICON_STYLE,
                color: "var(--trebble-primary, #EC7F54)",
              }}
            />
            <span
              style={{
                fontSize: "var(--text-base, 14px)",
                color: "var(--trebble-primary, #EC7F54)",
                flex: 1,
              }}
            >
              {t(ti18n, "SEARCH_FOR")} &apos;{searchQuery}&apos;
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export type { SearchSuggestion };
export default LibrarySearchBar;
